import { useState } from 'react';
import { Pencil, Trash2, Plus, ChevronLeft, Check } from 'lucide-react';
import { DEFAULT_PERSONALITY } from '../../../lib/characterCards.js';

const AVATAR_OPTIONS = ['✦', '✧', '🌙', '★', '☾', '⋆', '✺', '✸'];

/* ---------- 角色卡列表 ---------- */
export function CharactersList({
  characters,
  activeId,
  onActivate,
  onEdit,
  onDelete,
  onNew,
  onClose,
}) {
  return (
    <>
      <label className="ch-settings-label">
        全部角色卡 · <span className="ch-en">all characters</span>
      </label>

      {characters.length === 0 && (
        <div className="cc-empty">还没有角色卡 · 新建第一张</div>
      )}

      <div className="cc-list">
        {characters.map((c) => (
          <div
            key={c.id}
            className={'cc-card' + (c.id === activeId ? ' on' : '')}
          >
            <button
              className="cc-card-main"
              onClick={() => onActivate(c.id)}
              title={c.id === activeId ? '当前已激活' : '点击激活'}
            >
              <span className="cc-card-avatar">{c.avatar || '✦'}</span>
              <div className="cc-card-meta">
                <div className="cc-card-name">
                  {c.name}
                  {c.id === activeId && (
                    <span className="cc-card-on-tag">✦ 当前</span>
                  )}
                </div>
                <div className="cc-card-bio">
                  {(c.personality || '（无人格描述）').slice(0, 50)}
                  {c.personality && c.personality.length > 50 ? '……' : ''}
                </div>
              </div>
            </button>
            <div className="cc-card-tools">
              <button
                className="cc-icon-btn"
                onClick={() => onEdit(c.id)}
                title="编辑"
                aria-label="编辑"
              >
                <Pencil size={13} strokeWidth={1.6} />
              </button>
              <button
                className="cc-icon-btn cc-icon-danger"
                onClick={() => onDelete(c.id)}
                title="删除"
                aria-label="删除"
              >
                <Trash2 size={13} strokeWidth={1.6} />
              </button>
            </div>
          </div>
        ))}
      </div>

      <button className="cc-add" onClick={onNew}>
        <Plus size={14} strokeWidth={1.8} /> 新建角色卡
      </button>

      <div className="ch-settings-actions">
        <button className="ch-tool-btn on" onClick={onClose}>
          完 成
        </button>
      </div>
    </>
  );
}

/* ---------- 角色卡编辑器 ---------- */
export function CharacterEditor({ initial, onBack, onSave }) {
  const [draft, setDraft] = useState(initial);

  function patch(p) {
    setDraft((d) => ({ ...d, ...p }));
  }

  return (
    <>
      <div className="cc-editor-top">
        <button className="cc-back" onClick={onBack}>
          <ChevronLeft size={14} strokeWidth={1.6} /> 返回
        </button>
        <span className="cc-editor-title">编辑角色卡</span>
      </div>

      <label className="ch-settings-label cc-row-label">
        名 字 · <span className="ch-en">name</span>
      </label>
      <input
        className="ch-settings-input ch-settings-input-sm"
        value={draft.name}
        onChange={(e) => patch({ name: e.target.value })}
        placeholder="Sael"
      />

      <label className="ch-settings-label cc-row-label">
        头 像 · <span className="ch-en">avatar</span>
      </label>
      <div className="cc-avatar-row">
        {AVATAR_OPTIONS.map((a) => (
          <button
            key={a}
            type="button"
            className={
              'cc-avatar-opt' + (draft.avatar === a ? ' on' : '')
            }
            onClick={() => patch({ avatar: a })}
          >
            {a}
          </button>
        ))}
        <input
          className="cc-avatar-custom"
          value={draft.avatar}
          onChange={(e) => patch({ avatar: e.target.value.slice(0, 2) })}
          placeholder="自定义"
        />
      </div>

      <label className="ch-settings-label cc-row-label">
        人 格 · <span className="ch-en">personality</span>
      </label>
      <textarea
        className="ch-settings-input"
        value={draft.personality}
        onChange={(e) => patch({ personality: e.target.value })}
        rows={7}
        placeholder={DEFAULT_PERSONALITY}
      />

      <label className="ch-settings-label cc-row-label">
        场 景 · <span className="ch-en">scenario</span>
        <span className="cc-row-opt">可选</span>
      </label>
      <textarea
        className="ch-settings-input"
        value={draft.scenario}
        onChange={(e) => patch({ scenario: e.target.value })}
        rows={3}
        placeholder="你们在哪、什么时间、什么关系……"
      />

      <label className="ch-settings-label cc-row-label">
        示例对话 · <span className="ch-en">example dialogue</span>
        <span className="cc-row-opt">可选</span>
      </label>
      <textarea
        className="ch-settings-input"
        value={draft.example_dialogue}
        onChange={(e) => patch({ example_dialogue: e.target.value })}
        rows={4}
        placeholder="铃：……&#10;Sael：……"
      />

      <label className="ch-settings-label cc-row-label">
        补充指令 · <span className="ch-en">extra system prompt</span>
        <span className="cc-row-opt">可选</span>
      </label>
      <textarea
        className="ch-settings-input"
        value={draft.system_prompt}
        onChange={(e) => patch({ system_prompt: e.target.value })}
        rows={3}
        placeholder="比如：回答尽量带 ✦ 或 ✧ 符号"
      />

      <div className="ch-settings-actions">
        <button className="ch-tool-btn" onClick={onBack}>
          取 消
        </button>
        <button className="ch-tool-btn on" onClick={() => onSave(draft)}>
          <Check size={13} strokeWidth={1.8} /> 保 存
        </button>
      </div>
    </>
  );
}

